import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { JSX, useEffect, useState } from "react";
import { BiTrash } from "react-icons/bi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import PopoverMenu from "@/components/ui/Popeover";
import { Edit, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

export default function ManageAdCategoriesAdTypes(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [adTypeToDelete, setAdTypeToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const navigate = useNavigate();
  const {
    getAdCategoryDetailData,
    adCategoryDetailData,
    setEditData,
    deleteAdCategoryAdType,
  } = useAdCategoryStore(
    useShallow((state) => ({
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      adCategoryDetailData: state.adCategoryDetailData,
      setEditData: state.setEditData,
      deleteAdCategoryAdType: state.deleteAdCategoryAdType,
    }))
  );

  const setError = useGlobalStore((state) => state.setError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const [searchParams] = useSearchParams();

  const fetchData = async () => {
    try {
      await getAdCategoryDetailData(searchParams.get("id"));
    } catch (error) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!adTypeToDelete) return;

    try {
      await deleteAdCategoryAdType(adTypeToDelete.id);
      await fetchData();
      showSuccess("Ad type archived successfully");
      setAdTypeToDelete(null);
    } catch (error) {
      setError(error.message ?? "Failed to delete ad type");
    }
  };

  const createMenuItems = (adCategoryId: string, adInputName: string) => [
    {
      label: "Edit Ad Type",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        navigate(`/create-ad-categories?id=${adCategoryId}&step=3`);
      },
      showDivider: true,
    },
    {
      label: "Delete Ad Type",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () => setAdTypeToDelete({ id: adCategoryId, name: adInputName }),
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  if (!searchParams.get("id")) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          Oops!!! The Ad Type you are looking is not found.
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return <Spinner className="flex justify-centers" />;
  }

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-center mb-5">
          <h1 className="font-semibold text-3xl">Manage Ad Types</h1>
        </div>

        <div className="pt-2">
          <table className="w-full mt-5">
            <thead className="border-b border-gray-300 text-gray-600/50 text-sm">
              <tr>
                <th className="text-left py-2">Publisher</th>
                <th className="text-left py-2">Ad Input Name</th>
                <th className="text-left py-2">Media Type</th>
                <th className="text-left py-2">Parameters Count</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {!adCategoryDetailData ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-500">
                    No Data Found
                  </td>
                </tr>
              ) : (
                adCategoryDetailData?.ad_types?.flatMap((adType) =>
                  adType.ad_inputs.map((input, idx) => {
                    return (
                      <tr
                        key={`${
                          adType?.publisher_details?.id ?? "unknown"
                        }-${idx}`}
                        className="border-b border-gray-200"
                      >
                        <td className="py-4 font-semibold">
                          {adType?.publisher_details?.name ?? "N/A"}
                          <div className="text-xs text-gray-400 font-normal">
                            (
                            {adType?.publisher_details?.type === "single"
                              ? "Single Publisher"
                              : adType?.publisher_details?.type === "multi"
                              ? "Multiple Publisher"
                              : "User Defined"}
                            )
                          </div>
                        </td>
                        <td className="py-4">
                          <span className="bg-gray-100 px-2 py-1 text-xs">
                            {input?.name ?? "N/A"}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className="px-2 py-1 text-xs">
                            {input?.media_type
                              ? input.media_type.replace("_", " ").toUpperCase()
                              : "N/A"}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className="px-2 py-1 text-xs">
                            {input?.parameters?.length ?? 0}
                          </span>
                        </td>
                        <td className="text-right">
                          <PopoverMenu
                            items={createMenuItems(
                              adCategoryDetailData?.id,
                              input?.name
                            )}
                          />
                        </td>
                      </tr>
                    );
                  })
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!adTypeToDelete}
        onOpenChange={() => setAdTypeToDelete(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Delete Ad Type
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{adTypeToDelete?.name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setAdTypeToDelete(null)}>
              Cancel
            </Button>
            <Button
              type="secondary"
              trailingIcon={<BiTrash fontSize={18} />}
              onClick={handleDelete}
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
